/**
 * Unified Lead Selectors — Single Source of Truth
 *
 * Every lead/pipeline view MUST derive its data through these selectors.
 * The canonical dataset is salesOpportunities filtered to pre-appointment stages.
 */

import { parseISO, differenceInDays, differenceInHours } from "date-fns";
import type { Opportunity } from "./opportunityTypes";
import type { Conversation, Message } from "./communicationTypes";
import {
  scoreLeadConversion,
  type LeadTemperature,
} from "./conversionIntelligence";
import {
  calculateResponseMetrics,
  calculateLeadPriority,
  type LeadResponseMetrics,
  type LeadPriorityScore,
  type SLAStatus,
} from "./speedToLead";

/* ═══════ ENRICHED LEAD ═══════ */

export interface EnrichedLead {
  lead: Opportunity;
  temperature: LeadTemperature;
  conversionScore: number;
  responseMetrics: LeadResponseMetrics;
  priority: LeadPriorityScore;
  slaStatus: SLAStatus;
  isBooked: boolean;
  isContacted: boolean;
  isStale: boolean;
  needsResponse: boolean;
  ageDays: number;
}

/* ═══════ DERIVATION ═══════ */

export function deriveLeadEnrichment(
  lead: Opportunity,
  conversations: Conversation[],
  messages: Message[],
): EnrichedLead {
  const responseMetrics = calculateResponseMetrics(lead, conversations, messages);
  const priority = calculateLeadPriority(lead, responseMetrics);
  const scoring = scoreLeadConversion(lead, conversations, messages);

  const isBooked = lead.stage === "Appointment Scheduled" && !!lead.appointmentTime;
  const isContacted = (lead.activities?.length || 0) > 0 ||
    conversations.some((c) => c.linkedLeadId === lead.id);
  const ageDays = differenceInDays(new Date(), new Date(lead.createdAt));
  const isStale = ageDays > 7 && !isBooked && lead.stage === "Lead";
  const needsResponse = !isContacted && lead.stage === "Lead";

  return {
    lead,
    temperature: scoring.temperature,
    conversionScore: scoring.score,
    responseMetrics,
    priority,
    slaStatus: responseMetrics.slaStatus,
    isBooked,
    isContacted,
    isStale,
    needsResponse,
    ageDays,
  };
}

/* ═══════ CORE ENRICHMENT ═══════ */

export interface LeadSelectorDeps {
  salesOpportunities: Opportunity[];
  conversations: Conversation[];
  messages: Message[];
}

export function getAllLeads(deps: LeadSelectorDeps): EnrichedLead[] {
  const { salesOpportunities, conversations, messages } = deps;
  return salesOpportunities
    .filter((o) => o.stage === "Lead" || o.stage === "Appointment Scheduled")
    .map((o) => deriveLeadEnrichment(o, conversations, messages));
}

/* ═══════ SELECTORS ═══════ */

export function getHotLeads(all: EnrichedLead[]): EnrichedLead[] {
  return all.filter((l) => l.temperature === "hot");
}

export function getWarmLeads(all: EnrichedLead[]): EnrichedLead[] {
  return all.filter((l) => l.temperature === "warm");
}

export function getColdLeads(all: EnrichedLead[]): EnrichedLead[] {
  return all.filter((l) => l.temperature === "cold");
}

export function getStaleLeads(all: EnrichedLead[]): EnrichedLead[] {
  return all.filter((l) => l.isStale).sort((a, b) => b.ageDays - a.ageDays);
}

export function getBookedLeads(all: EnrichedLead[]): EnrichedLead[] {
  return all.filter((l) => l.isBooked);
}

export function getUnbookedLeads(all: EnrichedLead[]): EnrichedLead[] {
  return all.filter((l) => !l.isBooked && l.lead.stage === "Lead");
}

export function getLeadsNeedingResponse(all: EnrichedLead[]): EnrichedLead[] {
  return all.filter((l) => l.needsResponse).sort((a, b) => b.priority.score - a.priority.score);
}

export function getLeadsByRep(all: EnrichedLead[], repName: string): EnrichedLead[] {
  return all.filter((l) => l.lead.assignedRep === repName);
}

export function getLeadsBySLA(all: EnrichedLead[], status: SLAStatus): EnrichedLead[] {
  return all.filter((l) => l.slaStatus === status);
}

/** Priority queue: leads sorted by priority score descending */
export function getPriorityQueue(all: EnrichedLead[]): EnrichedLead[] {
  return [...all]
    .filter((l) => l.lead.stage === "Lead" && !l.isBooked)
    .sort((a, b) => b.priority.score - a.priority.score);
}

/** Summary stats for lead pipeline */
export function getLeadStats(all: EnrichedLead[]) {
  return {
    total: all.length,
    hot: all.filter((l) => l.temperature === "hot").length,
    warm: all.filter((l) => l.temperature === "warm").length,
    cold: all.filter((l) => l.temperature === "cold").length,
    booked: all.filter((l) => l.isBooked).length,
    unbooked: all.filter((l) => !l.isBooked && l.lead.stage === "Lead").length,
    stale: all.filter((l) => l.isStale).length,
    needsResponse: all.filter((l) => l.needsResponse).length,
    missedSLA: all.filter((l) => l.slaStatus === "missed").length,
  };
}
